import { defineStore } from 'pinia'
import { v4 as uuid } from 'uuid'
import { useI18n } from 'vue-i18n'
import type {
  PageResponse,
  PageData,
  EditorBlock,
  PageBlock,
  BlockTemplate,
} from '~/models/page-editor'
import PageEditorTitleBlock from '~/components/page-editor/title'
import PageEditorImagesBlock from '~/components/page-editor/images'
import PageEditorMarkdownBlock from '~/components/page-editor/markdown'
import PageEditorContentBlock from '~/components/page-editor/content'
import PageEditorTicketsBlock from '~/components/page-editor/tickets'
import PageEditorSpeakersBlock from '~/components/page-editor/speakers'
import PageEditorTextColumnsBlock from '~/components/page-editor/text-columns'
import PageEditorCardsBlock from '~/components/page-editor/cards'
import PageEditorProgramBlock from '~/components/page-editor/program'
import PageEditorVideoBlock from '~/components/page-editor/video'

interface StateInterface {
  page: PageData | null
  isProcessing: boolean
  isEditMode: boolean
  isLoaded: boolean
  block: PageBlock | null
  isLoadedByAdmin: boolean
  templates: BlockTemplate[]
}

export const usePageEditor = defineStore('page-editor', () => {
  const baseURL = useRuntimeConfig().public.gatewayApi
  const adminURL = useRuntimeConfig().public.gatewayAdminApi
  const { locale } = useI18n()
  const nuxtApp = useNuxtApp()

  const state = reactive<StateInterface>({
    page: null,
    isProcessing: false,
    isEditMode: false,
    isLoaded: false,
    block: null,
    isLoadedByAdmin: false,
    templates: [],
  })

  const blocksList: Partial<EditorBlock>[] = [
    {
      type: 1,
      ...PageEditorTitleBlock,
    },
    {
      type: 2,
      ...PageEditorImagesBlock,
    },
    {
      type: 3,
      ...PageEditorMarkdownBlock,
    },
    {
      type: 4,
      ...PageEditorContentBlock,
    },
    {
      type: 5,
      ...PageEditorTicketsBlock,
    },
    {
      type: 6,
      ...PageEditorSpeakersBlock,
    },
    {
      type: 7,
      ...PageEditorCardsBlock,
    },
    {
      type: 8,
      ...PageEditorTextColumnsBlock,
    },
    {
      type: 9,
      ...PageEditorProgramBlock,
    },
    {
      type: 10,
      ...PageEditorVideoBlock,
    },
  ]

  const blocksMap = blocksList.reduce((map, item) => {
    map[item.type as number] = item
    return map
  }, {} as { [key: number]: any })

  const pageBlockToEditorBlock = (block: PageBlock) => {
    return {
      ...block,
      ...blocksMap[block.type],
      ...{ editorData: JSON.parse(block.content) },
      uuid: block.uuid ?? uuid(),
      key: Symbol('key'),
    }
  }

  const editorDataToContent = (editorData: any) => {
    if (editorData.name) {
      editorData.name = editorData.name.toLowerCase()
    }

    return JSON.stringify(editorData)
  }

  const editorBlock: ComputedRef<EditorBlock> = computed(() => {
    if (state.block) {
      return pageBlockToEditorBlock(state.block)
    }

    return null
  })

  const editorBlocks: ComputedRef<EditorBlock[]> = computed(() => {
    if (state.page?.blocks) {
      return state.page?.blocks.map(pageBlockToEditorBlock)
    }

    return []
  })

  const isUrlIncludesLocale = (url: string) => {
    return url.includes(`/${locale.value}/`)
  }

  const removeLocaleFromUrl = (url: string) => {
    return url.replace(`/${locale.value}`, '')
  }

  // the whole page functions
  const fetchPage = async (
    url: string,
    isAdmin: boolean,
    checkPrev = true,
    showError = false
  ) => {
    const oldUrl = state.page?.url?.toLowerCase() || ''
    const newUrl = url.toLowerCase()

    if (checkPrev) {
      if (nuxtApp.isHydrating) {
        if (removeLocaleFromUrl(oldUrl) === removeLocaleFromUrl(newUrl)) {
          return
        }
      } else if (oldUrl === newUrl) {
        return
      }
    }

    state.isProcessing = true
    state.page = null

    try {
      const data = await useAuthFetch<PageResponse>('/page', {
        baseURL: isAdmin ? adminURL : baseURL,
        params: {
          url,
        },
      })

      state.page = data
    } catch (error: any) {
      if (error.status === 404 && showError) {
        throw useCreateScreenError({ message: 'page is not found' })
      } else if (error.status === 404 && isUrlIncludesLocale(url) && !isAdmin) {
        await fetchPage(removeLocaleFromUrl(url), isAdmin)
        return
      } else if (error.status !== 404) {
        useLogError(error)
      }

      state.page = { url }
    } finally {
      state.isLoaded = true
      state.isProcessing = false
      state.isLoadedByAdmin = isAdmin
    }
  }

  const createPage = async (url: string) => {
    state.isProcessing = true

    try {
      const data = await useAuthFetch<PageResponse>('/page', {
        baseURL: adminURL,
        method: 'POST',
        body: {
          url,
          // we create page with empty data, then we just put blocks inside "blocks" field
          // "data" field is for supporting old interface
          data: '[]',
        },
      })

      state.page = data
    } catch (error) {
      useLogError(error, true)
    } finally {
      state.isProcessing = false
    }
  }

  const deletePage = async (id: number) => {
    state.isProcessing = true
    try {
      await useAuthFetch(`/page/${id}`, {
        baseURL: adminURL,
        method: 'DELETE',
      })
      state.page = null
    } catch (error) {
      useLogError(error, true)
    } finally {
      state.isProcessing = false
    }
  }

  // blocks functions
  const fetchPageBlock = async (id: string) => {
    if (state.block?.uuid === id) {
      return
    }

    const { data } = await useAuthFetch<{ data: PageBlock }>(
      `/pages/blocks/${id}`,
      {
        baseURL,
      }
    )

    state.block = data
  }

  const getPageBlock = async (uuid: string, isAdmin = false) => {
    const { data } = await useAuthFetch<{ data: PageBlock }>(
      `/pages/blocks/${uuid}`,
      {
        baseURL: isAdmin ? adminURL : baseURL,
      }
    )

    return data
  }

  const createPageBlock = async (block: EditorBlock, pageId: number) => {
    const { data } = await useAuthFetch<{ data: PageBlock }>('/pages/blocks', {
      baseURL: adminURL,
      method: 'POST',
      body: {
        ...block,
        content: editorDataToContent(block.editorData),
        page_id: pageId,
        is_visible: true,
      },
    })

    return data
  }

  const updatePageBlock = async (block: Partial<EditorBlock>) => {
    const { data } = await useAuthFetch<{ data: PageBlock }>(
      `/pages/blocks/${block.uuid}`,
      {
        baseURL: adminURL,
        method: 'PUT',
        body: {
          ...block,
          content: editorDataToContent(block.editorData),
        },
      }
    )

    return data
  }

  const deletePageBlock = async (uuid: string) => {
    await useAuthFetch(`/pages/blocks/${uuid}`, {
      baseURL: adminURL,
      method: 'DELETE',
    })
  }

  async function updateSort(blocks: PageBlock[]) {
    if (!blocks.every((block) => block.id)) {
      console.warn('there are blocks without id')
      return
    }

    const items = blocks.map((block, index) => ({
      id: block.id,
      priority: index + 1,
    }))

    await useAuthFetch('/pages/blocks/sort', {
      baseURL: adminURL,
      method: 'PUT',
      body: { items },
    })
  }

  // templates functions
  const fetchBlockTemplateList = async () => {
    if (state.templates.length) {
      return
    }

    const { data } = await useAuthFetch<{ data: BlockTemplate[] }>(
      '/block-templates',
      {
        baseURL: adminURL,
        params: {
          per_page: 100,
        },
      }
    )

    state.templates = data
  }

  const createBlockTemplate = async (template: Partial<BlockTemplate>) => {
    const data = await useAuthFetch<BlockTemplate>('/block-templates', {
      baseURL: adminURL,
      method: 'POST',
      body: template,
    })

    state.templates.push(data)
  }

  const deleteBlockTemplate = async (templateId: number) => {
    await useAuthFetch(`/block-templates/${templateId}`, {
      baseURL: adminURL,
      method: 'DELETE',
    })

    state.templates = state.templates.filter(
      (template) => template.id !== templateId
    )
  }

  const turnOnEditMode = () => {
    state.isEditMode = true
  }

  const turnOffEditMode = () => {
    state.isEditMode = false
  }

  const toggleEditMode = () => {
    state.isEditMode = !state.isEditMode
  }

  const resetPage = () => {
    state.isLoaded = false
    state.page = null
  }

  function getBlockId(block: EditorBlock) {
    return block.editorData.name || 'block-' + block.uuid
  }

  return {
    page: toRef(state, 'page'),
    isEditMode: toRef(state, 'isEditMode'),
    isProcessing: toRef(state, 'isProcessing'),
    isLoaded: toRef(state, 'isLoaded'),
    block: toRef(state, 'block'),
    isLoadedByAdmin: toRef(state, 'isLoadedByAdmin'),
    templates: toRef(state, 'templates'),
    editorBlocks,
    editorBlock,
    blocksList,
    blocksMap,
    getBlockId,
    fetchPage,
    createPage,
    deletePage,
    resetPage,
    turnOnEditMode,
    turnOffEditMode,
    toggleEditMode,
    fetchPageBlock,
    getPageBlock,
    createPageBlock,
    updatePageBlock,
    deletePageBlock,
    updateSort,
    pageBlockToEditorBlock,
    fetchBlockTemplateList,
    createBlockTemplate,
    deleteBlockTemplate,
  }
})
